import { useFetch } from "@hyper-fetch/react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { HandCoins, List } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { sdk } from "@/api/sdk";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FinanceActions } from "@/features/finance/modules/finance-shell/finance-shell";
import { PaymentDetailSheet } from "@/features/finance/modules/payment-detail/payment-detail-sheet";
import { PaymentsTable } from "@/features/finance/modules/payments-table/payments-table";
import { RecordPaymentDialog } from "@/features/finance/modules/record-payment/record-payment-dialog";
import { ToCollectList } from "@/features/finance/modules/to-collect/to-collect-list";
import { hasPermissionInList } from "@/features/navigation/filter-nav-entries";
import { m } from "@/paraglide/messages.js";
import { PermissionAction, PermissionSubject } from "@complete-athlete/enums";

const TABS = ["to-collect", "all"] as const;

export const Route = createFileRoute("/(private)/finances/payments/")({
  beforeLoad: async () => {
    const { data } = await sdk.users.me.$get.send();
    if (!hasPermissionInList(data?.permissions ?? null, PermissionAction.READ, PermissionSubject.FINANCE)) {
      throw redirect({ to: "/" });
    }
  },
  loader: () => ({ crumb: m.finance_payments_title() }),
  validateSearch: z.object({ paymentId: z.string().optional(), tab: z.enum(TABS).optional().catch(undefined) }),
  component: PaymentsPage,
});

/** Every payment in one place: what is still owed first, then everything that came in, online or by hand. */
function PaymentsPage() {
  const { paymentId, tab = "to-collect" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [recording, setRecording] = useState(false);
  const { data: settings } = useFetch(sdk.booking.settings.$get);
  const open = (id: string | undefined) => void navigate({ search: (current) => ({ ...current, paymentId: id }) });

  return (
    <>
      <FinanceActions>
        <Button type="button" data-hero-primary onClick={() => setRecording(true)}>
          <HandCoins aria-hidden />
          {m.finance_record_title()}
        </Button>
      </FinanceActions>
      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({ search: (current) => ({ ...current, tab: value as (typeof TABS)[number] }) })
        }
      >
        <TabsList>
          <TabsTrigger value="to-collect">
            <HandCoins aria-hidden />
            {m.finance_payments_tab_to_collect()}
          </TabsTrigger>
          <TabsTrigger value="all">
            <List aria-hidden />
            {m.finance_payments_tab_all()}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="to-collect" className="mt-4">
          <ToCollectList />
        </TabsContent>
        <TabsContent value="all" className="mt-4">
          <PaymentsTable onOpen={open} />
        </TabsContent>
      </Tabs>
      <PaymentDetailSheet paymentId={paymentId ?? null} onClose={() => open(undefined)} />
      {recording ? (
        <RecordPaymentDialog
          open
          onOpenChange={setRecording}
          target={null}
          suggestedAmount={null}
          currency={settings?.currency ?? "PLN"}
        />
      ) : null}
    </>
  );
}
